import SlideManager from "./slide-manager";

const matrix = new THREE.Matrix4();
const invMatrix = new THREE.Matrix4();
const aspectMatrix = new THREE.Matrix4();
const aspectMul = new THREE.Vector2();
const aspectRayPos = new THREE.Vector2();
const USE_SIMPLE_GEO = true;
const tmpPos = new THREE.Vector3();
const tmpSize = new THREE.Vector3();
const tmpGeometry = (function () {
    /**
     *     5--------6
     *    /|       /|
     *   / |      / |
     *  4--------7  |
     *  |  |     |  |
     *  |  0 --- |- 1
     *  | /      | /
     *  |/       |/
     *  3--------2
     */

    const initialState = Object.freeze([
        -0.5, -0.5, -0.5, // 0
        +0.5, -0.5, -0.5, // 1
        +0.5, -0.5, +0.5, // 2
        -0.5, -0.5, +0.5, // 3
        -0.5, +0.5, +0.5, // 4
        -0.5, +0.5, -0.5, // 5
        +0.5, +0.5, -0.5, // 6
        +0.5, +0.5, +0.5  // 7
    ]);

    const geometry = new THREE.BufferGeometry();
    const attrPosition = new THREE.Float32BufferAttribute([...initialState], 3);

    const attrIndex = new THREE.Uint8BufferAttribute([
        0, 1, 3,
        3, 1, 2,
        1, 0, 6,
        6, 0, 5,
        1, 7, 2,
        7, 1, 6,
        5, 7, 6,
        7, 5, 4,
        0, 4, 5,
        4, 0, 3,
        4, 3, 2,
        2, 7, 4
    ], 1);

    geometry.addAttribute("position", attrPosition);
    geometry.setIndex(attrIndex);

    geometry.resetState = function () {
        attrPosition.copyArray(initialState);
    }

    return geometry;
})();

const vPos = [];
for (let i = 0; i < 3; i++) {
    vPos[i] = new THREE.Vector3();
}
const edgePoints = [];
for (let i = 0; i < 2; i++) {
    edgePoints[i] = new THREE.Vector3();
}
const facePoints = [];
for (let i = 0; i < 3; i++) {
    facePoints[i] = new THREE.Vector3();
}

class PassByRaycaster {
    constructor(camera, radius, loader) {
        this.camera = camera;
        this.rayPos = new THREE.Vector2();
        this.radius = radius;
        this._loader = loader;
    }

    setRayPos(pos) {
        this.rayPos.copy(pos);
    }

    setRadius(radius) {
        this.radius = radius;
    }

    raycastObject(object, intersections) {
        let aspect = this.camera.aspect;
        aspectMul.set(Math.max(1, aspect), Math.min(1, 1 / this.camera.aspect));

        aspectMatrix.set(
            aspectMul.x, 0, 0, 0,
            0, aspectMul.y, 0, 0,
            0, 0, 1, 0,
            0, 0, 0, 1
        );

        matrix.copy(aspectMatrix).multiply(this.camera.projectionMatrix).multiply(this.camera.matrixWorldInverse);
        invMatrix.getInverse(matrix);

        aspectRayPos.copy(this.rayPos);
        aspectRayPos.multiply(aspectMul);

        if (!intersections) {
            intersections = [];
        } else {
            intersections.length = 0;
        }

        let t0 = Date.now();
        this._addIntersections(object, intersections);
        let t1 = Date.now();
        intersections.sort((i1, i2) => i1.depth - i2.depth);
        let t2 = Date.now();
        /*let lines = [
            'cast: '+(t1-t0)+'ms',
            'sort: '+(t2-t1)+'ms',
            'total: '+(t2-t0)+'ms',
        ];
        console.log(lines.join('\n'));*/

        return intersections;
    }

    _addIntersections(object, intersections) {
        if (!object.visible) return;

        if (object.isMesh) {
            this._raycastMesh(object, intersections);
        }

        for (let child of object.children) {
            this._addIntersections(child, intersections);
        }
    }

    /**
     * 
     * @param {THREE.Mesh} mesh 
     * @param {[]} intersections 
     */
    _raycastMesh(mesh, intersections) {
        const geometry = USE_SIMPLE_GEO
            ? (function () {
                if (mesh.geometry.boundingBoxAnimated) {
                    mesh.geometry.boundingBoxAnimated.getCenter(tmpPos);
                    mesh.geometry.boundingBoxAnimated.getSize(tmpSize);
                } else {
                    if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
                    mesh.geometry.boundingBox.getCenter(tmpPos);
                    mesh.geometry.boundingBox.getSize(tmpSize);
                }

                tmpGeometry.resetState();
                tmpGeometry.scale(tmpSize.x, tmpSize.y, tmpSize.z);
                tmpGeometry.translate(tmpPos.x, tmpPos.y, tmpPos.z);

                return tmpGeometry;
            })()
            : mesh.geometry;

        const loader = this._loader;

        let vertexGetter = makeVertexGetter(mesh, geometry);
        let vertexCount = 0, edgeCount = 0, faceCount = 0;

        let t0 = Date.now();

        const dataObject = SlideManager.currentSlide.animation[mesh.uuid];

        const scaleFactor = dataObject && dataObject.extendedSelectionEnabled
            ? "extendedSelectionSize" in dataObject
                ? dataObject.extendedSelectionSize
                : 1
            : 1;

        for (let vid of iterateUniqueVertices(loader, geometry)) {
            vertexCount++;
            let pos = vertexGetter.getVertex(vid, vPos[0]).applyMatrix4(mesh.matrixWorld);
            let intersection = getPointIntersection(matrix, invMatrix, aspectRayPos, this.radius * scaleFactor, pos);
            if (intersection) {
                intersections.push({ object: mesh, type: 'vertex', vertex: vid, depth: intersection.depth, position: intersection.position });
            }
        }

        let t1 = Date.now();

        for (let edge of iterateUniqueEdges(loader, geometry)) {
            edgeCount++;
            for (let [i, vid] of edge.entries()) {
                vertexGetter.getVertex(vid, edgePoints[i]).applyMatrix4(mesh.matrixWorld);
            }

            let intersection = getLineIntersection(matrix, invMatrix, aspectRayPos, this.radius * scaleFactor, edgePoints);
            if (intersection) {
                intersections.push({ object: mesh, type: 'edge', vertices: [...edge], depth: intersection.depth, position: intersection.position });
            }
        }
        let t2 = Date.now();

        for (let face of iterateUniqueFaces(loader, geometry)) {
            faceCount++;
            for (let [i, vid] of face.entries()) {
                vertexGetter.getVertex(vid, facePoints[i]).applyMatrix4(mesh.matrixWorld);
            }

            let intersection = getTriangleIntersection(matrix, invMatrix, aspectRayPos, facePoints);
            if (intersection) {
                intersections.push({ object: mesh, type: 'face', vertices: [...face], depth: intersection.depth, position: intersection.position });
            }
        }

        let t3 = Date.now();

        /*let lines = [
            'vertexCount = '+vertexCount,
            'edgeCount = '+edgeCount,
            'faceCount = '+faceCount,
            'vertices: '+(t1-t0)+'ms',
            'edges: '+(t2-t1)+'ms',
            'faces: '+(t3-t2)+'ms',
        ];
        console.log(lines.join('\n'));*/
    }
}



function iterateVertices(geometry) {
    let count;

    if (geometry.isBufferGeometry) {
        count = geometry.getAttribute('position').count;
    } else {
        count = geometry.vertices.length;
    }

    const value = { value: null };
    let id = 0;

    return {
        next: function () {
            if (id === count) {
                return { done: true };
            } else {
                value.value = id++;
                return value;
            }
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };
}

const FaceKeys = ['a', 'b', 'c'];

function iterateIndices(geometry) {
    if (geometry.isBufferGeometry) {
        let position = geometry.getAttribute('position');
        let index = geometry.getIndex();

        if (index) {
            return index.array[Symbol.iterator]();
        } else {
            const count = position.count;
            const value = { value: null };
            let id = 0;

            return {
                next: function () {
                    if (id === count) {
                        return { done: true };
                    } else {
                        value.value = id++;
                        return value;
                    }
                },

                [Symbol.iterator]: function () {
                    return this;
                },
            };
        }
    } else {
        const value = { value: null };
        let fCount = geometry.faces.length;
        let fid = 0;
        let i = 0;

        return {
            next: function () {
                if (fid === fCount) {
                    return { done: true };
                } else {
                    let face = geometry.faces[fid];

                    value.value = face[FaceKeys[i++]];
                    if (i === 3) {
                        i = 0;
                        fid++;
                    }

                    return value;
                }
            },

            [Symbol.iterator]: function () {
                return this;
            },
        };
    }
}

function iterateFaces(geometry) {
    const faceIndices = [];
    const value = { value: faceIndices };
    const indexIter = iterateIndices(geometry);

    return {
        next: function () {
            for (let i = 0; i < 3; i++) {
                let indexVal = indexIter.next();
                if (indexVal.done) {
                    return { done: true };
                }

                faceIndices[i] = indexVal.value;
            }

            return value;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };
}

function iterateEdges(geometry) {
    let faceIndices;
    const edgeIndices = [];
    const value = { value: edgeIndices };
    const faceIter = iterateFaces(geometry);

    let i0 = 0;

    return {
        next: function () {
            if (i0 === 0) {
                let faceVal = faceIter.next();
                if (faceVal.done) {
                    return { done: true };
                }

                faceIndices = faceVal.value;
            }

            let i1 = (i0 + 1) % 3;
            edgeIndices[0] = faceIndices[i0];
            edgeIndices[1] = faceIndices[i1];

            i0 = i1;

            return value;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };
}



function buildUniqueVertices(geometry) {
    if (!geometry.isBufferGeometry) {
        throw new Error('must be BufferGeometry');
    }

    let posAttrib = geometry.getAttribute('position');
    let count = posAttrib.count;
    let attributes = [posAttrib.array];

    let morphPos = geometry.morphAttributes.position;
    if (morphPos) {
        for (let morphAttrib of morphPos) {
            attributes.push(morphAttrib.array);
        }
    }

    let skinIndex = geometry.getAttribute('skinIndex');
    let skinWeight = geometry.getAttribute('skinWeight');
    let skinIndexArray = skinIndex && skinIndex.array;
    let skinWeightArray = skinWeight && skinWeight.array;
    let skinning = skinIndexArray && skinWeightArray

    let weightsArray = [];
    let posIndex = {};
    let posStrArray = [];
    let uniqueIndices = [];
    let remapIndices = [];

    let index = 0;
    const notDoneValue = { progress: index, total: count };
    const done = { done: true };
    const notDone = { value: notDoneValue };

    const iterator = {
        next: function () {
            if (index === count) {
                return done;
            }

            let offset3 = index * 3;

            posStrArray.length = 0;
            for (let array of attributes) {
                posStrArray.push(array[offset3 + 0], array[offset3 + 1], array[offset3 + 2]);
            }

            if (skinning) {
                let offset4 = index * 4;

                let weights = {};
                for (let i = 0; i < 4; i++) {
                    let bIndex = skinIndexArray[offset4 + i];
                    let weight = skinWeightArray[offset4 + i];
                    if (weight !== 0) {
                        weights[bIndex] = (weights[bIndex] || 0) + weight
                    }
                }

                weightsArray.length = 0;
                for (let bIndex in weights) {
                    weightsArray.push([bIndex, weights[bIndex]])
                }

                weightsArray.sort((w1, w2) => w1.bIndex - w2.bIndex);

                for (let [bIndex, weight] of weightsArray) {
                    posStrArray.push(bIndex, weight);
                }
            }

            let posStr = posStrArray.join(' ');

            if (!(posStr in posIndex)) {
                posIndex[posStr] = index;
                uniqueIndices.push(index);
            }

            remapIndices[index] = posIndex[posStr];

            index++;
            notDoneValue.progress = index;

            return notDone;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };

    return { uniqueIndices, remapIndices, iterator };
}

function buildUniqueFaces(geometry, remapIndices) {
    if (!geometry.isBufferGeometry) {
        throw new Error('must be BufferGeometry');
    }

    const faceIt = iterateFaces(geometry);
    const faces = [];
    const addedFaces = {};

    const faceUnique = [];
    const indexSort = (a, b) => a - b;

    let index = 0;
    const notDoneValue = {};
    const done = { done: true };
    const notDone = { value: notDoneValue };

    const iterator = {
        next: function () {
            const itValue = faceIt.next();
            if (itValue.done) {
                return done;
            }

            const face = itValue.value;

            for (let i = 0; i < 3; i++) {
                faceUnique[i] = remapIndices[face[i]];
            }

            let repeating = false;
            for (let i = 0; i < 3; i++) {
                if (faceUnique[i] === faceUnique[(i + 1) % 3]) {
                    repeating = true;
                    break;
                }
            }

            if (!repeating) {
                faceUnique.sort(indexSort);
                let faceStr = faceUnique.join(' ');
                if (!addedFaces[faceStr]) {
                    faces.push(...faceUnique);
                    addedFaces[faceStr] = true;
                }
            }

            return notDone;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };

    return { faces, iterator };
}

function buildUniqueEdges(geometry, remapIndices, faces) {
    if (!geometry.isBufferGeometry) {
        throw new Error('must be BufferGeometry');
    }

    const edges = [];
    const addedEdges = {};
    const end = faces.length - 2;

    const edgeUnique = [];
    const indexSort = (a, b) => a - b;

    let offset = 0;
    const notDoneValue = { progress: offset, total: faces.length };
    const done = { done: true };
    const notDone = { value: notDoneValue };

    const iterator = {
        next: function () {
            if (offset >= end) {
                return done;
            }

            for (let i0 = 0; i0 < 3; i0++) {
                let i1 = (i0 + 1) % 3;
                edgeUnique[0] = remapIndices[faces[offset + i0]];
                edgeUnique[1] = remapIndices[faces[offset + i1]];

                if (edgeUnique[0] !== edgeUnique[1]) {
                    edgeUnique.sort(indexSort);
                    let edgeStr = edgeUnique.join(' ');
                    if (!addedEdges[edgeStr]) {
                        edges.push(...edgeUnique);
                        addedEdges[edgeStr] = true;
                    }
                }
            }

            offset += 3;
            notDoneValue.progress = offset;

            return notDone;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };

    return { edges, iterator };
}



function buildUniqueGeometryPrimitives(geometry) {
    let faces, faceIt;
    let edges, edgeIt;

    const done = { done: true };
    const notDone = {};

    let { uniqueIndices, remapIndices, iterator: vertexIt } = buildUniqueVertices(geometry);

    let step = 'vertices';

    const primitives = {};

    const iterator = {
        next: function () {
            switch (step) {
                case 'vertices': {
                    let itValue = vertexIt.next();
                    if (itValue.done) {
                        primitives.uniqueIndices = uniqueIndices;
                        step = 'faces';
                        ({ faces, iterator: faceIt } = buildUniqueFaces(geometry, remapIndices));
                    }
                    return notDone;
                }

                case 'faces': {
                    let itValue = faceIt.next();
                    if (itValue.done) {
                        primitives.faces = faces;
                        step = 'edges';
                        ({ edges, iterator: edgeIt } = buildUniqueEdges(geometry, remapIndices, faces));
                    }
                    return notDone;
                }

                case 'edges': {
                    let itValue = edgeIt.next();
                    if (itValue.done) {
                        primitives.edges = edges;
                        return done;
                    }
                    return notDone;
                }
            }
        },

        [Symbol.iterator]: function () {
            return this;
        },
    };

    return { primitives, iterator };
}



class Loader {
    constructor() {
        this._geometries = new WeakSet();
        this._primitives = new WeakMap();

        this._geoQueueItem = new WeakMap();
        this._queueFirst = null;
        this._queueLast = null;
    }

    addGeometry(geometry) {
        if (this._geometries.has(geometry)) {
            throw new Error('geometry already added');
        }

        this._geometries.add(geometry);

        let item = buildUniqueGeometryPrimitives(geometry);
        let queueItem = { primitives: item.primitives, iterator: item.iterator, geometry, prev: null, next: null };

        let last = this._queueLast;
        if (last) {
            last.next = queueItem;
            queueItem.prev = last;
        } else {
            this._queueFirst = queueItem;
            queueItem.startTime = Date.now();
        }
        this._queueLast = queueItem;
        this._geoQueueItem.set(geometry, queueItem);
    }

    removeGeometry(geometry) {
        if (!this._geometries.has(geometry)) {
            throw new Error('geometry not added');
        }

        this._geometries.delete(geometry);

        if (this._primitives.has(geometry)) {
            this._primitives.delete(geometry);
        } else {
            let queueItem = this._geoQueueItem.get(geometry);
            this._geoQueueItem.delete(geometry);

            let { prev, next } = queueItem;
            if (prev) {
                prev.next = next;
            } else {
                this._queueFirst = next;
            }
            if (next) {
                next.prev = prev;
            } else {
                this._queueLast = prev;
            }
        }
    }

    resumeLoading(stopTimestamp) {
        while (this._queueFirst && Date.now() < stopTimestamp) {
            let { primitives, iterator, geometry, prev, next, startTime } = this._queueFirst;

            while (Date.now() < stopTimestamp) {
                let itValue = iterator.next();
                if (itValue.done) {
                    //console.log('loaded:', geometry, 'time: '+(Date.now()-startTime)+' ms');
                    this._geoQueueItem.delete(geometry);
                    this._primitives.set(geometry, primitives);
                    this._queueFirst = next;
                    if (next) {
                        next.prev = null;
                        next.startTime = Date.now();
                    } else {
                        this._queueLast = null;
                    }
                    break;
                }
            }
        }
    }

    getGeometry(geometry) {
        if (!this._primitives.has(geometry)) {
            if (!this._geometries.has(geometry)) {
                this.addGeometry(geometry);
            }

            let queueItem = this._geoQueueItem.get(geometry);
            this._geoQueueItem.delete(geometry);

            let { primitives, iterator, prev, next, startTime } = queueItem;
            if (prev) {
                prev.next = next;
            } else {
                this._queueFirst = next;
            }
            if (next) {
                next.prev = prev;
            } else {
                this._queueLast = prev;
            }

            if (startTime === undefined) {
                startTime = Date.now();
            }

            for (let i of iterator) { };
            this._primitives.set(geometry, primitives);

            //console.log('loaded:', geometry, 'time: '+(Date.now()-startTime)+' ms');
        }

        return this._primitives.get(geometry);
    }
}



const Empty = [];

function iterateUniqueVertices(loader, geometry) {
    const primitives = loader.getGeometry(geometry);
    const uniqueIndices = primitives ? primitives.uniqueIndices : Empty;
    return uniqueIndices[Symbol.iterator]();
}

function iterateUniqueEdges(loader, geometry) {
    const primitives = loader.getGeometry(geometry);
    const indices = primitives ? primitives.edges : Empty;

    const edgeIndices = [];
    const value = { value: edgeIndices };
    let offset = 0, end = indices.length - 1;

    return {
        next: function () {
            if (offset >= end) {
                return { done: true };
            }

            edgeIndices[0] = indices[offset + 0];
            edgeIndices[1] = indices[offset + 1];
            offset += 2;

            return value;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    }
}

function iterateUniqueFaces(loader, geometry) {
    const primitives = loader.getGeometry(geometry);
    const indices = primitives ? primitives.faces : Empty;

    const faceIndices = [];
    const value = { value: faceIndices };
    let offset = 0, end = indices.length - 2;

    return {
        next: function () {
            if (offset >= end) {
                return { done: true };
            }

            faceIndices[0] = indices[offset + 0];
            faceIndices[1] = indices[offset + 1];
            faceIndices[2] = indices[offset + 2];
            offset += 3;

            return value;
        },

        [Symbol.iterator]: function () {
            return this;
        },
    }
}



class GeometryVertexGetter {
    constructor(geometry) {
        this.vertices = geometry.vertices;
    }

    getVertex(id, targetPos) {
        return targetPos.copy(this.vertices[id]);
    }
}

class BufferGeometryVertexGetter {
    constructor(geometry) {
        this.vertices = geometry.getAttribute('position').array;
    }

    getVertex(id, targetPos) {
        let vertices = this.vertices, offset = id * 3;
        return targetPos.set(vertices[offset + 0], vertices[offset + 1], vertices[offset + 2]);
    }
}

const addMorphPos = new THREE.Vector3();

class MorphGeometryVertexGetter {
    constructor(object, geometry) {
        this.vertices = geometry.vertices;
        this.morphTargets = [];

        let baseInfluence = 1;

        if (geometry.morphTargets && object.morphTargetInfluences) {
            let morphTargetCount = Math.min(geometry.morphTargets.length, object.morphTargetInfluences.length);
            for (let i = 0; i < morphTargetCount; i++) {
                let influence = object.morphTargetInfluences[i];
                if (influence !== 0) {
                    let vertices = geometry.morphTargets.vertices;
                    this.morphTargets.push({ influence, vertices });
                    baseInfluence -= influence;
                }
            }
        }

        this.baseInfluence = baseInfluence;
    }

    getVertex(id, targetPos) {
        targetPos.copy(this.vertices[id]).multiplyScalar(this.baseInfluence);

        for (let { influence, vertices } of this.morphTargets) {
            addMorphPos.copy(vertices[id]).multiplyScalar(influence);
            targetPos.add(addMorphPos);
        }

        return targetPos;
    }
}

class MorphBufferGeometryVertexGetter {
    constructor(object, geometry) {
        this.vertices = geometry.getAttribute('position').array;
        this.morphTargets = [];

        let baseInfluence = 1;

        if (geometry.morphAttributes && geometry.morphAttributes.position && object.morphTargetInfluences) {
            for (let name in object.morphTargetDictionary) {
                let i = object.morphTargetDictionary[name];
                let influence = object.morphTargetInfluences[i];
                if (influence !== 0) {
                    let vertices = geometry.morphAttributes.position[i].array;
                    this.morphTargets.push({ influence, vertices });

                    if (!geometry.morphTargetsRelative) {
                        baseInfluence -= influence;
                    }
                }
            }
        }

        this.baseInfluence = baseInfluence;
    }

    getVertex(id, targetPos) {
        let offset = id * 3;

        {
            let vertices = this.vertices;
            targetPos.set(vertices[offset + 0], vertices[offset + 1], vertices[offset + 2]);
            targetPos.multiplyScalar(this.baseInfluence);
        }

        for (let { influence, vertices } of this.morphTargets) {
            addMorphPos.set(vertices[offset + 0], vertices[offset + 1], vertices[offset + 2]);
            addMorphPos.multiplyScalar(influence);
            targetPos.add(addMorphPos);
        }

        return targetPos;
    }
}

const unskinnedPos = new THREE.Vector3();
const bindPos = new THREE.Vector3();
const addSkinPos = new THREE.Vector3();

const MissingSkeleton = { bones: [], boneInverses: [] };
const MissingMatrix = new THREE.Matrix4();
const MissingBone = { matrixWorld: MissingMatrix };

class SkinnedBufferGeometryVertexGetter {
    constructor(unskinnedGetter, object, geometry) {
        this.unskinnedGetter = unskinnedGetter;

        this.object = object;

        const skinIndex = geometry.getAttribute('skinIndex');
        const skinWeight = geometry.getAttribute('skinWeight');

        this.skinIndex = (skinIndex && skinIndex.array) || [];
        this.skinWeight = (skinWeight && skinWeight.array) || [];
    }

    getVertex(id, targetPos) {
        targetPos.set(0, 0, 0);
        this.unskinnedGetter.getVertex(id, unskinnedPos);

        const { skeleton, bindMatrix, bindMatrixInverse } = this.object;
        const { bones, boneInverses } = (skeleton && !USE_SIMPLE_GEO || MissingSkeleton);

        bindPos.copy(unskinnedPos).applyMatrix4(bindMatrix);

        const offset = id * 4;

        for (let i = 0; i < 4; i++) {
            const index = this.skinIndex[offset + i];
            const weight = this.skinWeight[offset + i];
            const boneInverse = boneInverses[index] || MissingMatrix;
            const boneMatrix = (bones[index] || MissingBone).matrixWorld;

            addSkinPos
                .copy(bindPos)
                .applyMatrix4(boneInverse)
                .applyMatrix4(boneMatrix)
                .multiplyScalar(weight === undefined ? 1 : weight);

            targetPos.add(addSkinPos);
        }

        targetPos.applyMatrix4(bindMatrixInverse);

        return targetPos;
    }
}

function makeUnskinnedVertexGetter(object, geometry) {
    let influences = object.morphTargetInfluences;
    let hasMorph = influences && influences.length > 0;

    if (geometry.isBufferGeometry) {
        if (hasMorph) {
            return new MorphBufferGeometryVertexGetter(object, geometry);
        } else {
            return new BufferGeometryVertexGetter(geometry);
        }
    } else {
        if (hasMorph) {
            return new MorphGeometryVertexGetter(object, geometry);
        } else {
            return new GeometryVertexGetter(geometry);
        }
    }
}

function makeVertexGetter(object, geometry) {
    let unskinnedGetter = makeUnskinnedVertexGetter(object, geometry);

    if (object.isSkinnedMesh && !USE_SIMPLE_GEO) {
        return new SkinnedBufferGeometryVertexGetter(unskinnedGetter, object, geometry);
    } else {
        return unskinnedGetter;
    }
}

const getPointIntersection = (function () {
    const rPoint4 = new THREE.Vector4();
    const rPoint2 = new THREE.Vector2();
    const intersectPos4 = new THREE.Vector4();
    const g_position = new THREE.Vector3();

    return function (matrix, invMatrix, rayPos, radius, point) {
        rPoint4.set(point.x, point.y, point.z, 1).applyMatrix4(matrix);
        if (rPoint4.z < -1) {
            return null;
        }

        let w = rPoint4.w;
        rPoint2.set(rPoint4.x, rPoint4.y).divideScalar(w);
        rPoint2.sub(rayPos);

        if (rPoint2.lengthSq() <= radius * radius) {
            intersectPos4.set(rayPos.x * w, rayPos.y * w, rPoint4.z, w);
            intersectPos4.applyMatrix4(invMatrix);

            let position = g_position.set(intersectPos4.x, intersectPos4.y, intersectPos4.z);
            position.divideScalar(intersectPos4.w);

            return { position, depth: rPoint4.z / w };
        } else {
            return null;
        }
    }
})();

const getLineIntersection = (function () {
    const rPoints4 = [], rPoints2 = [];

    for (let i = 0; i < 2; i++) {
        rPoints4[i] = new THREE.Vector4();
        rPoints2[i] = new THREE.Vector2();
    }

    const direction = new THREE.Vector2();
    const normal = new THREE.Vector2();
    const rRayPos = new THREE.Vector2();
    const rClosestPoint = new THREE.Vector2();
    const closestPoint4 = new THREE.Vector4();
    const intersectPos4 = new THREE.Vector4();
    const g_position = new THREE.Vector3();

    return function (matrix, invMatrix, rayPos, radius, points) {
        for (let [i, point] of points.entries()) {
            let rPoint4 = rPoints4[i], rPoint2 = rPoints2[i];
            rPoint4.set(point.x, point.y, point.z, 1).applyMatrix4(matrix);
            let w = rPoint4.w;
            rPoint2.set(rPoint4.x, rPoint4.y).divideScalar(w);
        }

        if (rPoints4[0].z < -1 && rPoints4[1].z < -1) {
            return null;
        }

        direction.subVectors(rPoints2[1], rPoints2[0]);
        rRayPos.subVectors(rayPos, rPoints2[0]);

        let aProj = rRayPos.dot(direction) / direction.lengthSq();
        rClosestPoint.copy(direction).multiplyScalar(aProj).add(rPoints2[0]);

        let a;
        if (Math.abs(rRayPos.x) > Math.abs(rRayPos.y)) {
            a = (rPoints4[0].x - rClosestPoint.x * rPoints4[0].w) / (rClosestPoint.x * (rPoints4[1].w - rPoints4[0].w) - (rPoints4[1].x - rPoints4[0].x));
        } else {
            a = (rPoints4[0].y - rClosestPoint.y * rPoints4[0].w) / (rClosestPoint.y * (rPoints4[1].w - rPoints4[0].w) - (rPoints4[1].y - rPoints4[0].y));
        }

        if (a >= 0 && a <= 1) {
            normal.set(-direction.y, direction.x).normalize();
            let distance = Math.abs(normal.dot(rRayPos));
            if (distance > radius) {
                return null;
            }

            closestPoint4.lerpVectors(rPoints4[0], rPoints4[1], a);

            let w = closestPoint4.w;
            let depth = closestPoint4.z / w;
            if (depth < -1 || depth > 1) {
                return null;
            }

            intersectPos4.set(rayPos.x * w, rayPos.y * w, closestPoint4.z, w);
            intersectPos4.applyMatrix4(invMatrix);

            let position = g_position.set(intersectPos4.x, intersectPos4.y, intersectPos4.z);
            position.divideScalar(intersectPos4.w);

            return { position, depth };
        } else {
            return null;
        }
    }
})();

const getTriangleIntersection = (function () {
    const rPoints4 = [], rPoints2 = [];

    for (let i = 0; i < 3; i++) {
        rPoints4[i] = new THREE.Vector4();
        rPoints2[i] = new THREE.Vector2();
    }

    const aMatrix = new THREE.Matrix3();
    const aInvMatrix = new THREE.Matrix3();
    const v = new THREE.Vector3();
    const av = new THREE.Vector3();
    const a = [];
    const weightedPoint = new THREE.Vector4();
    const intersectPos4 = new THREE.Vector4();
    const g_position = new THREE.Vector3();

    return function (matrix, invMatrix, rayPos, points) {
        for (let [i, point] of points.entries()) {
            let rPoint4 = rPoints4[i], rPoint2 = rPoints2[i];
            rPoint4.set(point.x, point.y, point.z, 1).applyMatrix4(matrix);
            let w = rPoint4.w;
            rPoint2.set(rPoint4.x, rPoint4.y).divideScalar(w);
        }

        if (rPoints4[0].z < -1 && rPoints4[1].z < -1 && rPoints4[2].z < -1) {
            return null;
        }

        let x1 = rPoints4[0].x, y1 = rPoints4[0].y, w1 = rPoints4[0].w;
        let x2 = rPoints4[1].x, y2 = rPoints4[1].y, w2 = rPoints4[1].w;
        let x3 = rPoints4[2].x, y3 = rPoints4[2].y, w3 = rPoints4[2].w;

        aMatrix.set(
            rayPos.x * (w3 - w1) + x1 - x3, rayPos.x * (w3 - w2) + x2 - x3, 0,
            rayPos.y * (w3 - w1) + y1 - y3, rayPos.y * (w3 - w2) + y2 - y3, 0,
            0, 0, 1
        );

        aInvMatrix.getInverse(aMatrix);

        v.set(rayPos.x * w3 - x3, rayPos.y * w3 - y3, 0);
        av.copy(v).applyMatrix3(aInvMatrix);

        a[0] = av.x;
        a[1] = av.y;
        a[2] = 1 - av.x - av.y;

        if (a.every(ai => ai >= 0)) {
            intersectPos4.set(0, 0, 0, 0);
            for (let [i, ai] of a.entries()) {
                weightedPoint.copy(rPoints4[i]).multiplyScalar(ai);
                intersectPos4.add(weightedPoint);
            }

            let w = intersectPos4.w;
            let depth = intersectPos4.z / w;
            if (depth < -1 || depth > 1) {
                return null;
            }

            intersectPos4.applyMatrix4(invMatrix);

            let position = g_position.set(intersectPos4.x, intersectPos4.y, intersectPos4.z);
            position.divideScalar(intersectPos4.w);

            return { position, depth };
        } else {
            return null;
        }
    }
})();

export default PassByRaycaster;
export { PassByRaycaster, Loader };
